import React, { useEffect, useRef, useState } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';

import CustomPagination from 'components/Common/PaginationDataTable';
import Search from 'components/Search/Search';
import customStyles from 'components/FilterDataTable/CustomTableStyles';
import CustomExpander from 'components/FilterDataTable/CustomExpander/CustomExpander';
import { MLSelect } from 'components/Select/Select';

interface FilterTrapHistoryDataTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  title?: string;
  titlePerPage?: string;
  isLoading: boolean;
  amountPerPageLabel?: string;
  onChangePage: (page: number) => void;
  onChangeFilterParams: (ordering: string) => void;
  onChangeSearch: (search: string) => void;
  searchFields: string[];
  expandableRows?: boolean;
  expandableRowsComponent?: React.FC<{ data: T }>;
  expandableRowsComponentProps?: {
    slug: string;
  };
}

function FilterTrapHistoryDataTable<T>({
  columns,
  data,
  title,
  titlePerPage,
  onChangeFilterParams,
  onChangeSearch,
  searchFields,
  isLoading,
  expandableRows = false,
  expandableRowsComponent,
  expandableRowsComponentProps,
}: FilterTrapHistoryDataTableProps<T>) {
  const scrollToTop = useRef<HTMLDivElement>(null);
  const [filteredData, setFilteredData] = useState<T[]>(data);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  useEffect(() => {
    let filtered = data.filter(item =>
      searchFields.some(column => {
        const value = item[column as keyof T];
        return (
          value === null || (value !== undefined && value.toString().toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }),
    );

    if (sortField) {
      filtered = filtered.sort((a, b) => {
        const aValue = a[sortField as keyof T];
        const bValue = b[sortField as keyof T];

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredData(filtered);
  }, [searchTerm, data, sortField, sortDirection, searchFields]);

  // useEffect(() => {
  //   currentPage !== 1 && scrollToTop.current?.scrollIntoView();
  // }, [currentPage]);

  const handleItemsPerPageChange = (value: string | number | null) => {
    const selectedValue = Number(value) || 10;
    setItemsPerPage(selectedValue);
    setCurrentPage(1);
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    onChangeSearch(search);
    setCurrentPage(1);
  };

  const handleSort = (column: TableColumn<T>, sortDirection: 'asc' | 'desc') => {
    setSortField(column.sortField);
    setSortDirection(sortDirection);
    onChangeFilterParams(sortDirection === 'asc' ? column.sortField || '' : `-${column.sortField}`);
    setCurrentPage(1);
  };

  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="p-3" ref={scrollToTop}>
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <MLSelect
          options={[
            { value: '10', label: '10' },
            { value: '20', label: '20' },
            { value: '50', label: '50' },
            { value: '100', label: '100' },
          ]}
          label={titlePerPage ? titlePerPage : 'Devices Per Page'}
          onChange={handleItemsPerPageChange}
          value={String(itemsPerPage)}
        />
        <div className="d-flex align-items-center">
          <h5 className="fw-bold mx-2">{title ? title : 'Trap history'}</h5>
        </div>

        <div className="d-flex">
          <Search onSearch={handleSearch} />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={paginatedData}
        progressPending={isLoading}
        pagination={false}
        customStyles={customStyles}
        dense
        onSort={handleSort}
        sortServer
        expandableRows={expandableRows}
        expandableRowsComponent={expandableRowsComponent}
        expandableRowsComponentProps={expandableRowsComponentProps}
        expandableIcon={{
          collapsed: <CustomExpander isExpanded={false} />,
          expanded: <CustomExpander isExpanded />,
        }}
      />
      <CustomPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={(page: number) => {
          setCurrentPage(page);
          scrollToTop.current?.scrollIntoView();
        }}
      />
    </div>
  );
}

export default FilterTrapHistoryDataTable;
